import { create } from 'zustand';

import { api } from 'src/utils/axios';

import { CollectiblesRepository } from 'src/repositories/benefits/collectibles.repository';

import { collectionDetailsItemType } from 'src/types/benefits/collectibles';

type States = {
  isLoading: boolean;
  isLoadingCollectiblesList: boolean;
  isLoadingUnlockItem: boolean;
  isLoadingCollectibleDetails: boolean;
  isLoadingRescueCollectible: boolean;
  isLoadingLevelPoints: boolean;
  isLoadingUnlockedItems: boolean;
  isLoadingVerifyActiveCollection: boolean;
  isLoadingCollectibleRedemptionDetails: boolean;
  levelPoints: number[];
  collection: any;
  collectibleDetails: collectionDetailsItemType | null;
  hasActiveCollection: boolean;
  collectibleRedemptionDetails: any;
};

type Actions = {
  getCollectibles: () => Promise<void>;
  unlockItem: (id: string) => Promise<void>;
  getCollectibleDetails: (id: string) => Promise<void>;
  rescueCollectible: (collectibleId: string) => Promise<any>;
  getLevelPoints: () => Promise<void>;
  getNewUnlockedItems: (collectionId: string) => Promise<any>;
  verifyActiveCollection: () => Promise<any>;
  getCollectibleRedemptionDetails: (collectibleRedemptionId: string) => Promise<any>;
};

type Collectibles = Actions & States;

const initialValues = {
  isLoading: false,
  isLoadingCollectiblesList: false,
  isLoadingUnlockItem: false,
  isLoadingCollectibleDetails: false,
  isLoadingRescueCollectible: false,
  isLoadingLevelPoints: false,
  isLoadingUnlockedItems: false,
  isLoadingVerifyActiveCollection: false,
  isLoadingCollectibleRedemptionDetails: false,
  levelPoints: [],
  collection: [],
  collectibleDetails: null,
  hasActiveCollection: false,
  collectibleRedemptionDetails: false
};

const collectiblesRepository = new CollectiblesRepository(api);

export const useCollectiblesStore = create<Collectibles>((set: any, get: any) => ({
  ...initialValues,

  getCollectibles: async () => {
    set({ isLoadingCollectiblesList: true });
    const response = await collectiblesRepository.getCollectiblesList();

    if (response.isLeft()) {
      set({ isLoadingCollectiblesList: false });
      throw new Error(response.value.message);
    } else {
      set({ collection: response.value, isLoadingCollectiblesList: false });
    }
  },

  unlockItem: async (id) => {
    set({ isLoadingUnlockItem: true });
    const response = await collectiblesRepository.unlockItem({ id });

    if (response.isLeft()) {
      set({ isLoadingUnlockItem: false });
      throw new Error(response.value.message);
    } else {
      set({ isLoadingUnlockItem: false });
    }
  },

  getCollectibleDetails: async (collectibleId) => {
    set({ isLoadingCollectibleDetails: true });
    const response = await collectiblesRepository.getCollectibleDetails(collectibleId);

    if (response.isLeft()) {
      set({ isLoadingCollectibleDetails: false });
      throw new Error(response.value.message);
    } else {
      set({ collectibleDetails: response.value, isLoadingCollectibleDetails: false });
    }
  },

  rescueCollectible: async (collectibleId) => {
    set({ isLoadingRescueCollectible: true });
    const response = await collectiblesRepository.rescueCollectible({ collectibleId });

    if (response.isLeft()) {
      set({ isLoadingRescueCollectible: false });
      throw new Error(response.value.message);
    } else {
      set({ isLoadingRescueCollectible: false });
      return response.value;
    }
  },

  getLevelPoints: async () => {
    set({ isLoadingLevelPoints: true });
    const response = await collectiblesRepository.getLevelPoints();

    if (response.isLeft()) {
      set({ isLoadingLevelPoints: false });
      throw new Error(response.value.message);
    } else {
      set({ levelPoints: response.value, isLoadingLevelPoints: false });
    }
  },

  getNewUnlockedItems: async (collectionId) => {
    set({ isLoadingUnlockedItems: true });
    const response = await collectiblesRepository.getNewUnlockedItems(collectionId);

    if (response.isLeft()) {
      set({ isLoadingUnlockedItems: false });
      throw new Error(response.value.message);
    } else {
      set({ isLoadingUnlockedItems: false });
      return response.value;
    }
  },

  verifyActiveCollection: async () => {
    set({ isLoadingVerifyActiveCollection: true });
    const response = await collectiblesRepository.verifyActiveCollection();

    if (response.isLeft()) {
      set({ isLoadingVerifyActiveCollection: false, hasActiveCollection: false });
      return false
    }
    set({ isLoadingVerifyActiveCollection: false, hasActiveCollection: true });
    return response.value;
  },

  getCollectibleRedemptionDetails: async (collectibleRedemptionId) => {
    set({ isLoadingCollectibleRedemptionDetails: true });
    const response = await collectiblesRepository.getCollectibleRedemptionDetails(collectibleRedemptionId);

    if (response.isLeft()) {
      set({ isLoadingCollectibleRedemptionDetails: false });
      throw new Error(response.value.message);
    } else {
      set({ collectibleRedemptionDetails: response.value, isLoadingCollectibleRedemptionDetails: false });
    }
  }
}));
